// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  FactoryWithChoiceContext,
  ChoiceContext as WithdrawContext,
} from '@digitalasset/allocation-instruction-api-ts-client';
import React, { useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  AllocationFactory,
  AllocationFactory_Allocate,
} from '@daml.js/splice-api-token-allocation-instruction/lib/Splice/Api/Token/AllocationInstructionV1';
import {
  Allocation,
  AllocationSpecification,
  SettlementInfo,
  TransferLeg,
} from '@daml.js/splice-api-token-allocation/lib/Splice/Api/Token/AllocationV1';
import { Holding as TokenStandardHolding } from '@daml.js/splice-api-token-holding/lib/Splice/Api/Token/HoldingV1';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import CopyToClipboard from '../../../components/CopyToClipboard';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useAllocationFactoryWithContext } from '../../../hooks/mutations/allocation/useAllocationFactoryWithContext';
import { useAllocationWithdrawContext } from '../../../hooks/mutations/allocation/useAllocationWithdrawContext';
import { useExerciseSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import InterfaceContract from '../../../utils/InterfaceContract';
import { fmt, parseDateString, toTokenStandardDisclosedContract } from '../../../utils/common';
import {
  ERR_CREATE_ALLOCATION,
  ERR_WITHDRAW_ALLOCATION,
  SUC_CREATE_ALLOCATION,
  SUC_WITHDRAW_ALLOCATION,
} from '../../../utils/strings';
import { PageWrapper } from '../../PageWrapper';
import { CreateAllocationConfirmationDialog } from './CreateAllocationConfirmationDialog';
import { WithdrawAllocationConfirmationDialog } from './WithdrawAllocationConfirmationDialog';
import { useGetAllocationRequestRows } from './useGetAllocationRequestRows';

type Payload = {
  settlement: SettlementInfo;
  transferLeg: TransferLeg;
};

type Row = {
  contractId: string;
  transferId: string;
  state: string;
  payload: Payload;
  inputHoldings: InterfaceContract<TokenStandardHolding>[];
  allocation?: InterfaceContract<Allocation>;
};

type Action = {
  name: string;
  action: () => Promise<unknown>;
  enabled: boolean;
};

type AllocationContext = {
  registrar: string;
  allocation: AllocationSpecification;
  inputHoldings: ContractId<TokenStandardHolding>[];
  factoryWithChoiceContext: FactoryWithChoiceContext;
};

type AllocationWithdrawContext = {
  registrar: string;
  allocationCid: ContractId<Allocation>;
  choiceContext: WithdrawContext;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action, enabled }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action()} disabled={!enabled}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'executor',
      headerName: 'Executor',
      renderCell: (params) => <PartyDisplay party={params.row.payload.settlement.executor} />,
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.settlement.executor,
    },
    {
      field: 'settlementReference',
      headerName: 'Settlement Reference',
      renderCell: (params) => {
        const ref = params.row.payload.settlement.settlementRef;
        return ref.cid || ref.id;
      },
      flex: 150,
      filterable: true,
      valueGetter: (params) => {
        const ref = params.row.payload.settlement.settlementRef;
        return ref.cid || ref.id;
      },
    },
    {
      field: 'requestedAt',
      headerName: 'Requested At',
      valueGetter: (params) =>
        parseDateString(params.row.payload.settlement.requestedAt).toLocaleString(),
      flex: 100,
      filterable: true,
    },
    {
      field: 'allocateBefore',
      headerName: 'Allocate Before',
      valueGetter: (params) =>
        parseDateString(params.row.payload.settlement.allocateBefore).toLocaleString(),
      flex: 100,
      filterable: true,
    },
    {
      field: 'settleBefore',
      headerName: 'Settle Before',
      valueGetter: (params) =>
        parseDateString(params.row.payload.settlement.settleBefore).toLocaleString(),
      flex: 100,
      filterable: true,
    },
    {
      field: 'sender',
      headerName: 'Sender',
      renderCell: (params) => <PartyDisplay party={params.row.payload.transferLeg.sender} />,
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.transferLeg.sender,
    },
    {
      field: 'receiver',
      headerName: 'Receiver',
      renderCell: (params) => <PartyDisplay party={params.row.payload.transferLeg.receiver} />,
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.transferLeg.receiver,
    },
    {
      field: 'instrumentAdmin',
      headerName: 'Instrument Admin',
      renderCell: (params) => (
        <PartyDisplay party={params.row.payload.transferLeg.instrumentId.admin} />
      ),
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.transferLeg.instrumentId.admin,
    },
    {
      field: 'instrumentId',
      headerName: 'Instrument id',
      renderCell: (params) => params.row.payload.transferLeg.instrumentId.id,
      flex: 50,
      filterable: true,
      valueGetter: (params) => params.row.payload.transferLeg.instrumentId.id,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      valueGetter: (params) => Number(params.row.payload.transferLeg.amount),
      renderCell: (params) => fmt(params.row.payload.transferLeg.amount, 2),
      flex: 50,
      filterable: true,
    },
    {
      field: 'allocation',
      headerName: 'Allocation',
      valueGetter: (params) =>
        params.row.allocation && (
          <CopyToClipboard
            buttonClassName="copy-button"
            tooltipText="Copy to clipboard"
            value={params.row.allocation.contractId}
          >
            {params.row.allocation.contractId}
          </CopyToClipboard>
        ),
      flex: 100,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 100,
      filterable: false,
    },
  ];
};

export const AllocationRequests = () => {
  const [allocationContext, setAllocationContext] = useState<AllocationContext | undefined>(
    undefined,
  );
  const [allocationWithdrawContext, setAllocationWithdrawContext] = useState<
    AllocationWithdrawContext | undefined
  >(undefined);
  const [dialogNameOpen, setDialogName] = React.useState<string | undefined>();
  const { submit: createAllocation } = useExerciseSubmission(
    AllocationFactory.AllocationFactory_Allocate,
    SUC_CREATE_ALLOCATION,
    ERR_CREATE_ALLOCATION,
  );
  const { submit: withdrawAllocation } = useExerciseSubmission(
    Allocation.Allocation_Withdraw,
    SUC_WITHDRAW_ALLOCATION,
    ERR_WITHDRAW_ALLOCATION,
  );
  const getAllocationFactoryWithContext = useAllocationFactoryWithContext();
  const getAllocationWithdrawContext = useAllocationWithdrawContext();

  const createActions = (r: Row) => {
    const actions: Action[] = [
      {
        name: 'Allocate',
        action: async () => {
          setDialogName('create');
          const payload = {
            registrar: r.payload.transferLeg.instrumentId.admin,
            allocation: {
              settlement: r.payload.settlement,
              transferLeg: r.payload.transferLeg,
              transferLegId: r.transferId,
            },
            inputHoldings: r.inputHoldings.map((holding) => holding.contractId),
          };
          const allocationFactoryWithContext =
            await getAllocationFactoryWithContext.mutateAsync(payload);
          setAllocationContext({
            ...payload,
            factoryWithChoiceContext: allocationFactoryWithContext,
          });
        },
        enabled: r.inputHoldings.length !== 0 && !r.allocation,
      },
    ];

    if (r.allocation) {
      actions.push({
        name: 'Withdraw',
        action: async () => {
          const payload = {
            registrar: r.payload.transferLeg.instrumentId.admin,
            allocationCid: r.allocation!.contractId,
          };
          setDialogName('withdraw');
          const withdrawContext = await getAllocationWithdrawContext.mutateAsync(payload);
          setAllocationWithdrawContext({
            ...payload,
            choiceContext: withdrawContext,
          });
        },
        enabled: !!r.allocation,
      });
    }
    return actions;
  };
  const { rows, isLoading } = useGetAllocationRequestRows() || [];

  if (isLoading) return <Loading />;

  const onCreateAllocation = async () => {
    if (allocationContext) {
      const payload: AllocationFactory_Allocate = {
        expectedAdmin: allocationContext.registrar,
        allocation: allocationContext.allocation,
        inputHoldingCids: allocationContext.inputHoldings,
        requestedAt: new Date().toISOString(),
        extraArgs: {
          context: allocationContext.factoryWithChoiceContext.choiceContext
            .choiceContextData as ChoiceContext,
          meta: { values: {} },
        },
      };
      const disclosedContracts =
        allocationContext?.factoryWithChoiceContext.choiceContext.disclosedContracts.map(
          toTokenStandardDisclosedContract,
        ) || [];
      await createAllocation({
        cid: allocationContext.factoryWithChoiceContext.factoryId as ContractId<AllocationFactory>,
        argument: payload,
        meta: {
          disclosedContracts,
        },
      });
      setDialogName(undefined);
    }
  };
  const onWithdrawAllocation = async () => {
    if (allocationWithdrawContext) {
      const payload = {
        extraArgs: {
          context: (allocationWithdrawContext.choiceContext.choiceContextData || {
            values: {},
          }) as ChoiceContext,
          meta: { values: {} },
        },
      };
      const disclosedContracts =
        allocationWithdrawContext.choiceContext.disclosedContracts.map(
          toTokenStandardDisclosedContract,
        ) || [];
      await withdrawAllocation({
        cid: allocationWithdrawContext.allocationCid,
        argument: payload,
        meta: {
          disclosedContracts,
        },
      });

      setDialogName(undefined);
    }
  };
  return (
    <PageWrapper pageName="Allocation Requests">
      <CreateAllocationConfirmationDialog
        open={!!allocationContext && dialogNameOpen === 'create'}
        onClose={() => {
          setAllocationContext(undefined);
          setDialogName(undefined);
        }}
        onCreateAllocation={onCreateAllocation}
      />
      <WithdrawAllocationConfirmationDialog
        open={!!allocationWithdrawContext && dialogNameOpen === 'withdraw'}
        onClose={() => {
          setAllocationWithdrawContext(undefined);
          setDialogName(undefined);
        }}
        onWithdraw={onWithdrawAllocation}
      />
      <DataTable
        columns={createHeaders(createActions)}
        rows={rows || []}
        rowKey={(a) => `${a.contractId}-${a.transferId}`}
      />
    </PageWrapper>
  );
};
